import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CoreButton, CorePagination, CoreSearchField, CoreTabs } from "@verity/components/core";
import { useContext, useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../../../helpers/api-client";
import { intIdToUuid } from "../../../../../helpers/uuid";
import i18n from "../../../../../i18n";
import { PortalControllerContext } from "../../../../context";
import { useIsAdmin } from "../../../../shared";
import InterviewInstructorWelcomePage from "../WelcomePage/InterviewInstructorWelcomePage";
import { FlagIcon } from "./assets";
import { ActiveIcon, CompletedIcon, TotalIcon, UpcomingIcon } from "./assets/interview-icons";
import DataCard from "./components/DataCard";
import InterviewCard from "./components/InterviewCard";
import TabLabel from "./components/TabLabel";
import styles from "./InterviewInstructorDashboard.module.css";
import InterviewDashboardSkeleton from "./InterviewInstructorDashboardSkeleton";
import { InterviewType, NonNullProctoringOptions, SessionType, Status } from "./types";

type CountsType = {
	total: number;
	upcoming: number;
	completed: number;
	live: number;
};

const PAGE_SIZE = 10;
const FIFTEEN_MINUTES = 15 * 60 * 1000;

function buildSessionRecord(sessions: SessionType[]): Record<string, SessionType> {
	if (!sessions) return {};

	const sessionRecord = sessions.reduce((acc: Record<string, SessionType>, session) => {
		if (!session.assessmentId) return acc;
		acc[session.assessmentId] = session;
		return acc;
	}, {});

	return sessionRecord;
}

function getStatus(start: Date, end: Date, now: Date = new Date()): Status {
	const joinTime = new Date(start.getTime() - FIFTEEN_MINUTES);

	if (now < joinTime) return Status.Upcoming;
	else if (now >= joinTime && now < start) return Status.ActiveUpcoming;
	else if (now >= start && now < end) return Status.Active;
	else return Status.Completed;
}

function getInterviewCounts(interviews: InterviewType[]): CountsType {
	let [total, upcoming, completed, live] = [0, 0, 0, 0];

	interviews.forEach((interview) => {
		total++;

		switch (interview.status) {
			case Status.Active:
			case Status.ActiveUpcoming:
				live++;
				break;
			case Status.Completed:
				completed++;
				break;
			case Status.Upcoming:
				upcoming++;
				break;
		}
	});
	return { total, upcoming, completed, live };
}

export default function InterviewInstructorDashboard() {
	const { portalContext, userInfo } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId, instituteUuid, courseUuid } = portalContext;

	const currentUserUuid = userInfo?.uuid;

	const searchControlsId = useId();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const {
		data: interviews,
		error,
		isLoading,
	} = useQuery({
		queryKey: ["institute-interviews", lmsProvidedCourseId, instituteUuid, courseUuid, currentUserUuid],
		enabled: !!lmsProvidedCourseId && !!instituteUuid && !!courseUuid && !!currentUserUuid,
		queryFn: async (): Promise<InterviewType[]> => {
			const [interviews, sessionRecord] = await Promise.all([
				apiClient.assessment.getCourseAssessments(lmsProvidedCourseId!),
				apiClient.liveClient
					.getLiveSessionsWithUsers({ instituteUuid: instituteUuid!, courseUuid: courseUuid! })
					.then((sessions) => buildSessionRecord(sessions?.results)),
			]);

			//Add live session and status to interview object
			return interviews
				.map((interview) => ({
					...interview,
					liveSession: sessionRecord[interview.quizId] ?? sessionRecord[intIdToUuid(Number(interview.id))] ?? null,
				}))
				.filter(
					(
						interview,
					): interview is typeof interview & {
						liveSession: SessionType;
						proctoringOptions: NonNullProctoringOptions;
					} =>
						!!interview.liveSession &&
						interview.liveSession.users.filter((user) => user.role).length >= 1 &&
						interview.liveSession.users.some((user) => isAdmin || user.userId === currentUserUuid!) &&
						!!interview.proctoringOptions &&
						!!interview.proctoringOptions.candidateInterviewOptions,
				)
				.map((interview) => ({
					...interview,
					status: getStatus(new Date(interview.liveSession.startTime), new Date(interview.liveSession.endTime)),
				}));
		},
	});

	const [statusTick, setStatusTick] = useState(() => Date.now());
	const [interviewCounts, setInterviewCounts] = useState<CountsType>({
		total: 0,
		upcoming: 0,
		completed: 0,
		live: 0,
	});
	const [filteredInterviewCounts, setFilteredInterviewCounts] = useState<CountsType>({
		total: 0,
		upcoming: 0,
		completed: 0,
		live: 0,
	});
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [tab, setTab] = useState<"all" | "completed" | "active" | "upcoming">("all");
	const [filteredInterviews, setFilteredInterviews] = useState<InterviewType[]>([]);

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setStatusTick(Date.now());
		}, 10000);

		return () => window.clearInterval(intervalId);
	}, []);

	const interviewsWithStatus = useMemo(() => {
		if (!interviews) return [];
		const now = new Date(statusTick);

		return interviews.map((interview) => ({
			...interview,
			status: getStatus(new Date(interview.liveSession.startTime), new Date(interview.liveSession.endTime), now),
		}));
	}, [interviews, statusTick]);

	//Update counts for cards
	useEffect(() => {
		const counts = getInterviewCounts(interviewsWithStatus);
		setInterviewCounts(counts);
	}, [interviewsWithStatus]);

	//Filter
	useEffect(() => {
		function isSearchMatch(search: string, interview: InterviewType) {
			const searchLower = search.toLowerCase();

			const candidateOptions = interview.proctoringOptions.candidateInterviewOptions;

			return (
				candidateOptions.candidateName.toLowerCase().includes(searchLower) ||
				candidateOptions.title.toLowerCase().includes(searchLower) ||
				candidateOptions.positionName.toLowerCase().includes(searchLower)
			);
		}

		const searchFiltered = interviewsWithStatus.filter(
			(interview) => !searchFilter || isSearchMatch(searchFilter, interview),
		);

		const filteredCount = getInterviewCounts(searchFiltered);
		setFilteredInterviewCounts(filteredCount);

		const filtered = searchFiltered
			.filter(
				(interview) =>
					tab === "all" || interview.status === tab || (tab === "active" && interview.status === Status.ActiveUpcoming),
			)
			.sort((a, b) => {
				// Put Completed interviews to the end
				if (a.status === Status.Completed && b.status !== Status.Completed) return 1;
				else if (a.status !== Status.Completed && b.status === Status.Completed) return -1;
				// sort completed in reverse chronological order, and others in chronological order
				if (a.status === Status.Completed && b.status === Status.Completed)
					return new Date(b.liveSession.startTime).getTime() - new Date(a.liveSession.startTime).getTime();
				else return new Date(a.liveSession.startTime).getTime() - new Date(b.liveSession.startTime).getTime();
			});
		setFilteredInterviews(filtered);
	}, [searchFilter, tab, interviewsWithStatus]);

	if (isLoading) return <InterviewDashboardSkeleton />;

	if (error) {
		console.error("Error fetching interviews:", error);
		throw new Error(error.message);
	}

	if (!interviews || interviews.length === 0) {
		return <InterviewInstructorWelcomePage />;
	}

	return (
		<div className={styles.pageWrapper}>
			<div className={styles.pageContainer}>
				<Box display="flex" width="100%" gap="20px">
					<DataCard
						title={i18n.t(`candidate.instructorDashboard.status.total`)}
						number={interviewCounts.total}
						icon={<TotalIcon />}
					/>
					<DataCard
						title={i18n.t(`candidate.instructorDashboard.status.upcoming`)}
						number={interviewCounts.upcoming}
						icon={<UpcomingIcon />}
					/>
					<DataCard
						title={i18n.t(`candidate.instructorDashboard.status.completed`)}
						number={interviewCounts.completed}
						icon={<CompletedIcon />}
					/>
					<DataCard
						title={i18n.t(`candidate.instructorDashboard.status.active`)}
						number={interviewCounts.live}
						icon={<ActiveIcon />}
					/>
				</Box>
				<div className={styles.dashboard}>
					<div className={styles.header}>
						<FlagIcon />
						<Typography>
							{i18n.t(`candidate.instructorDashboard.${isAdmin ? "adminTitle" : "instructorTitle"}`)}
						</Typography>
					</div>
					<div className={styles.dashboardBody}>
						<Typography>{i18n.t(`candidate.instructorDashboard.description`)}</Typography>

						{/* TODO ADD FILTER */}
						{/* <AssessmentFilterButton filter={filter} setFilter={setFilter} /> */}
						<Stack direction="row" gap="10px" alignItems="center">
							<Box width="583px">
								<CoreSearchField
									label={i18n.t(`candidate.instructorDashboard.searchPlaceholder`)}
									onSearch={(value) => {
										setPage(0);
										setSearchFilter(value);
									}}
									ariaControls={searchControlsId}
									totalResults={interviewCounts.total}
									showSearchButton
									value={searchFilter}
								/>
							</Box>
							<CoreButton onClick={() => navigate("/interviews/schedule")} style={{ flexShrink: 0 }}>
								{i18n.t(`candidate.scheduleInterview.buttonText`)}
							</CoreButton>
						</Stack>

						<CoreTabs
							tabs={[
								{
									key: "all",
									label: (
										<TabLabel
											text={i18n.t(`candidate.instructorDashboard.status.all`)}
											count={filteredInterviewCounts.total}
										/>
									),
								},
								{
									key: "upcoming",
									label: (
										<TabLabel
											text={i18n.t(`candidate.instructorDashboard.status.upcoming`)}
											count={filteredInterviewCounts.upcoming}
										/>
									),
								},
								{
									key: "completed",
									label: (
										<TabLabel
											text={i18n.t(`candidate.instructorDashboard.status.completed`)}
											count={filteredInterviewCounts.completed}
										/>
									),
								},
								{
									key: "active",
									label: (
										<TabLabel
											text={i18n.t(`candidate.instructorDashboard.status.active`)}
											count={filteredInterviewCounts.live}
										/>
									),
								},
							]}
							onChange={(_, newTab) => {
								setPage(0);
								setTab(newTab);
							}}
							value={tab}
						/>

						<Stack id={searchControlsId} spacing="15px">
							{filteredInterviews.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((interview) => (
								<InterviewCard key={interview.id} interview={interview} />
							))}
						</Stack>
						<Box display="flex" justifyContent="center">
							<CorePagination
								count={Math.ceil(filteredInterviews.length / PAGE_SIZE)}
								onChange={(_, p) => setPage(p - 1)}
								defaultPage={1}
								page={page + 1}
							/>
						</Box>
					</div>
				</div>
			</div>
		</div>
	);
}
